import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  Sparkles, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  Copy,
  Check,
  Download,
  X,
  Layers,
  Zap,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import BuildDeckPickerModal from './BuildDeckPickerModal';
import DetectionBadge from './DetectionBadge';

export default function VisionProposalGenerator({ opportunityId, opportunityData }) {
  const [showForm, setShowForm] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const api = useApi();
  const navigate = useNavigate();

  // Document 2 · Phase 3 — Auto-fill state (Phase 3.2 — live API)
  const [autoFillState, setAutoFillState] = useState('idle'); // idle | filling | filled | error
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  const [autoFillContext, setAutoFillContext] = useState(null);
  const [autoFillError, setAutoFillError] = useState(null);
  const [autoFillUsage, setAutoFillUsage] = useState(null);

  // Document 2 · Phase 4 — Build Vision Deck modal (Phase 4.1 hardcoded picker)
  const [buildDeckOpen, setBuildDeckOpen] = useState(false);

  // Document 2 · Phase 6 — Build & Send (one-click orchestrator)
  const [buildAndSending, setBuildAndSending] = useState(false);

  const handleBuildAndSend = async () => {
    if (!proposal?.proposal_id) {
      toast.error('No proposal to build from. Generate the proposal first.');
      return;
    }
    if (!opportunityId) {
      toast.error('No opportunity context available');
      return;
    }
    setBuildAndSending(true);
    try {
      const res = await api.post('/build-and-send/start', {
        proposal_id: proposal.proposal_id,
        opportunity_id: opportunityId,
        force_refresh: false,
      });
      if (res?.workflow_id) {
        if (res?.daily_usage?.soft_warn) {
          toast.warning(`Heads up — ${res.daily_usage.used_today}/${res.daily_usage.cap} outreach generations used today.`);
        } else {
          toast.success('Deck draft + dual-tone emails ready · review before sending');
        }
        navigate(`/build-and-send/review/${res.workflow_id}`);
      } else {
        toast.error('Build & Send returned no workflow id');
      }
    } catch (err) {
      const detail = err?.data?.detail;
      const msg = (detail && (detail.message || detail)) || err?.message || 'Build & Send failed';
      const partial = detail?.partial_deck;
      if (partial?.edit_url) {
        toast.error(`${typeof msg === 'string' ? msg : 'Build & Send failed'} · deck draft saved`);
        navigate(partial.edit_url);
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Build & Send failed');
      }
    } finally {
      setBuildAndSending(false);
    }
  };

  const handleBuildDeck = async (selection) => {
    if (!proposal?.proposal_id) {
      toast.error('No proposal to build from. Generate the proposal first.');
      return;
    }
    try {
      const res = await api.post('/vision-decks/from-proposal', {
        proposal_id: proposal.proposal_id,
        mode: selection.mode,
        template_id: selection.template_id,
        source_deck_id: selection.source_deck_id,
      });
      if (res?.deck_id && res?.edit_url) {
        toast.success('Deck draft created — opening editor');
        setBuildDeckOpen(false);
        navigate(res.edit_url);
      } else {
        toast.error('Build failed — no deck id returned');
      }
    } catch (err) {
      const msg = err?.data?.detail || err?.message || 'Build failed';
      toast.error(typeof msg === 'string' ? msg : 'Build failed');
    }
  };

  const [formData, setFormData] = useState({
    client_name: opportunityData?.company_name || '',
    property_name: opportunityData?.property_name || opportunityData?.company_name || '',
    industry: opportunityData?.property_type || 'Hospitality',
    core_problem: '',
    desired_transformation: '',
    proposed_solution: '',
    key_benefit: '',
    timeline: '30-60 days',
    departments: [],
    opportunity_id: opportunityId
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // User edited an auto-filled field → clear its "AI suggested" marker
    if (autoFilledFields.has(field)) {
      const next = new Set(autoFilledFields);
      next.delete(field);
      setAutoFilledFields(next);
    }
  };

  // Phase 3.2: Live API auto-fill from POST /sales-intelligence/vision-proposal/auto-fill
  const FIELD_KEYS = ['core_problem', 'desired_transformation', 'proposed_solution', 'key_benefit', 'timeline', 'departments'];

  const handleAutoFill = async (forceRefresh = false) => {
    if (!opportunityId) {
      toast.error('No opportunity context available');
      return;
    }
    setAutoFillState('filling');
    setAutoFillError(null);
    try {
      const res = await api.post('/sales-intelligence/vision-proposal/auto-fill', {
        opportunity_id: opportunityId,
        force_refresh: forceRefresh,
      });
      const fields = res?.fields || {};
      const filledKeys = FIELD_KEYS.filter((k) => {
        const v = fields[k];
        if (Array.isArray(v)) return v.length > 0;
        return typeof v === 'string' ? v.trim().length > 0 : v != null;
      });
      setFormData(prev => ({
        ...prev,
        core_problem: fields.core_problem || prev.core_problem,
        desired_transformation: fields.desired_transformation || prev.desired_transformation,
        proposed_solution: fields.proposed_solution || prev.proposed_solution,
        key_benefit: fields.key_benefit || prev.key_benefit,
        timeline: fields.timeline || prev.timeline,
        departments: Array.isArray(fields.departments) && fields.departments.length ? fields.departments : prev.departments,
      }));
      setAutoFilledFields(new Set(filledKeys));
      setAutoFillContext(res?.context || null);
      setAutoFillUsage(res?.daily_usage || null);
      setAutoFillState('filled');
      if (res?.daily_usage?.soft_warn) {
        toast.warning(`Heads up — ${res.daily_usage.used_today}/${res.daily_usage.cap} auto-fills used today.`);
      } else if (res?.cached) {
        toast.success('Loaded recent auto-fill suggestions');
      } else {
        toast.success('AI auto-fill ready · review before generating');
      }
    } catch (err) {
      const msg = err?.data?.detail?.message || err?.message || 'Auto-fill failed';
      setAutoFillError(msg);
      setAutoFillState('error');
      toast.error(msg);
    }
  };

  const handleClearAutoFill = () => {
    setFormData(prev => ({
      ...prev,
      core_problem: '',
      desired_transformation: '',
      proposed_solution: '',
      key_benefit: '',
      departments: [],
    }));
    setAutoFilledFields(new Set());
    setAutoFillContext(null);
    setAutoFillError(null);
    setAutoFillState('idle');
  };

  const generateProposal = async (overrides = null) => {
    if (!formData.core_problem || !formData.desired_transformation || !formData.proposed_solution) {
      toast.error('Please fill in the required fields');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (overrides) {
        if (overrides.override_business_scale) payload.override_business_scale = overrides.override_business_scale;
        if (overrides.override_detection_mode) payload.override_detection_mode = overrides.override_detection_mode;
        payload.force_refresh = true;
      }
      const data = await api.post('/sales-intelligence/vision-proposal', payload);
      
      if (data.error) {
        toast.error(data.error);
        return;
      }
      
      setProposal(data);
      setShowForm(false);
      if (overrides) {
        toast.success('Regenerated with override · saved on this opportunity');
      } else {
        toast.success('Vision Proposal generated');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate proposal');
    } finally {
      setLoading(false);
    }
  };

  const copyProposal = () => {
    if (!proposal?.proposal) return;
    
    const p = proposal.proposal;
    const text = `
VISION PROPOSAL: ${formData.client_name}

EXECUTIVE SUMMARY
${p.executive_summary}

THE OPPORTUNITY
${p.the_opportunity}

THE CHALLENGE
${p.the_challenge}

THE VISION
${p.the_vision}

THE PLAN
${Array.isArray(p.the_plan) ? p.the_plan.map((step, i) => `${i + 1}. ${step}`).join('\n') : p.the_plan}

THE RESULT
${Array.isArray(p.the_result) ? p.the_result.map(r => `• ${r}`).join('\n') : p.the_result}

NEXT STEP
${p.next_step}
    `.trim();
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Proposal copied to clipboard');
  };

  // Form view
  if (showForm) {
    return (
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                <FileText className="h-5 w-5 text-[#B89B5E]" />
              </div>
              <CardTitle className="text-lg">Vision Proposal Generator</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Document 2 · Phase 3 — Auto-fill banner */}
          {autoFillState === 'idle' && (
            <button
              type="button"
              onClick={() => handleAutoFill(false)}
              data-testid="autofill-cta"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm border border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50/50 to-transparent hover:from-amber-100 hover:via-amber-50 transition-colors text-left group"
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center ring-4 ring-amber-50 group-hover:ring-amber-100 transition-all">
                  <Sparkles className="h-4 w-4 text-amber-700" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-stone-800">Auto-fill from signal</p>
                  <p className="text-xs text-stone-500">
                    Pre-populate problem, transformation, solution, benefit, and impacted departments from this opportunity's context.
                  </p>
                </div>
              </div>
              <span className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 group-hover:text-amber-800 hidden sm:inline">
                Use AI →
              </span>
            </button>
          )}

          {autoFillState === 'filling' && (
            <div
              className="w-full flex items-center gap-3 px-4 py-3 rounded-sm border border-amber-200 bg-amber-50"
              data-testid="autofill-loading"
            >
              <RefreshCw className="h-4 w-4 text-amber-700 animate-spin" />
              <span className="text-sm text-stone-700">
                Reading signal context, property type, and contact role…
              </span>
            </div>
          )}

          {autoFillState === 'error' && (
            <div
              className="w-full rounded-sm border border-red-200 bg-red-50 p-4"
              data-testid="autofill-error"
            >
              <p className="text-sm font-semibold text-red-800">Auto-fill unavailable</p>
              <p className="text-xs text-red-700 mt-0.5">{autoFillError}</p>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleAutoFill(false)}
                  className="text-xs font-medium text-red-700 hover:text-red-900 underline-offset-2 hover:underline"
                  data-testid="autofill-retry"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={handleClearAutoFill}
                  className="text-xs font-medium text-stone-500 hover:text-stone-700 underline-offset-2 hover:underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {autoFillState === 'filled' && autoFillContext && (
            <div
              className="w-full rounded-sm border border-amber-200 bg-gradient-to-br from-amber-50 via-amber-50/40 to-transparent p-4"
              data-testid="autofill-banner"
            >
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center ring-4 ring-amber-50 flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-800">AI suggestions ready</p>
                  <p className="text-xs text-stone-600 mt-0.5">
                    Pre-filled from:{' '}
                    {[
                      autoFillContext.signal_type && (
                        <span key="st" className="font-medium text-amber-800">
                          {String(autoFillContext.signal_type).replace(/_/g, ' ')}
                        </span>
                      ),
                      autoFillContext.property_type,
                      autoFillContext.location,
                      autoFillContext.contact_role,
                    ]
                      .filter(Boolean)
                      .map((part, i, arr) => (
                        <span key={i}>
                          {part}
                          {i < arr.length - 1 ? ' · ' : ''}
                        </span>
                      ))}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge
                      variant="outline"
                      className="bg-white/80 border-amber-200 text-amber-800 text-[10px] uppercase tracking-wide font-semibold"
                    >
                      Draft · review before generating
                    </Badge>
                    {autoFillUsage && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-stone-500">
                        {autoFillUsage.used_today}/{autoFillUsage.cap} today
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleAutoFill(true)}
                      data-testid="autofill-refill"
                      className="text-xs font-medium text-amber-700 hover:text-amber-900 underline-offset-2 hover:underline"
                    >
                      Re-fill
                    </button>
                    <button
                      type="button"
                      onClick={handleClearAutoFill}
                      data-testid="autofill-clear"
                      className="text-xs font-medium text-stone-500 hover:text-stone-700 underline-offset-2 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Client Name</Label>
              <Input 
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
                placeholder="Acme Hotels"
              />
            </div>
            <div>
              <Label className="text-xs">Property Name</Label>
              <Input 
                value={formData.property_name}
                onChange={(e) => handleInputChange('property_name', e.target.value)}
                placeholder="Grand Plaza Hotel"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Industry</Label>
              <Input 
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                placeholder="Hospitality"
              />
            </div>
            <div>
              <Label className="text-xs">Timeline</Label>
              <Input 
                value={formData.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                placeholder="30-60 days"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Core Problem <span className="text-red-500">*</span></Label>
            <Textarea 
              value={formData.core_problem}
              onChange={(e) => handleInputChange('core_problem', e.target.value)}
              placeholder="What challenge is the client facing? What pain point needs solving?"
              rows={2}
            />
            {autoFilledFields.has('core_problem') && (
              <p className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="autofill-ribbon-core_problem">
                <Sparkles className="h-3 w-3" />
                AI-suggested · review before sending
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">Desired Transformation <span className="text-red-500">*</span></Label>
            <Textarea 
              value={formData.desired_transformation}
              onChange={(e) => handleInputChange('desired_transformation', e.target.value)}
              placeholder="What does success look like? What outcome do they want?"
              rows={2}
            />
            {autoFilledFields.has('desired_transformation') && (
              <p className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="autofill-ribbon-desired_transformation">
                <Sparkles className="h-3 w-3" />
                AI-suggested · review before sending
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">Proposed Solution <span className="text-red-500">*</span></Label>
            <Textarea 
              value={formData.proposed_solution}
              onChange={(e) => handleInputChange('proposed_solution', e.target.value)}
              placeholder="How will you solve their problem? What are you proposing?"
              rows={2}
            />
            {autoFilledFields.has('proposed_solution') && (
              <p className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="autofill-ribbon-proposed_solution">
                <Sparkles className="h-3 w-3" />
                AI-suggested · review before sending
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">Key Benefit</Label>
            <Input 
              value={formData.key_benefit}
              onChange={(e) => handleInputChange('key_benefit', e.target.value)}
              placeholder="The primary value they'll receive"
            />
            {autoFilledFields.has('key_benefit') && (
              <p className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="autofill-ribbon-key_benefit">
                <Sparkles className="h-3 w-3" />
                AI-suggested · review before sending
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs">Departments Impacted (comma-separated)</Label>
            <Input 
              value={formData.departments.join(', ')}
              onChange={(e) => handleInputChange('departments', e.target.value.split(',').map(d => d.trim()).filter(Boolean))}
              placeholder="Front Desk, Housekeeping, F&B"
            />
            {autoFilledFields.has('departments') && (
              <p className="mt-1 text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="autofill-ribbon-departments">
                <Sparkles className="h-3 w-3" />
                AI-suggested · review before sending
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              onClick={generateProposal} 
              disabled={loading}
              className="flex-1 bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Generate Vision Proposal
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Proposal view
  if (proposal) {
    const p = proposal.proposal;
    return (
      <>
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="vision-proposal">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 rounded-lg bg-[#B89B5E]/10 shrink-0">
                <FileText className="h-5 w-5 text-[#B89B5E]" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-lg">Vision Proposal</CardTitle>
                <p className="text-xs text-muted-foreground truncate">
                  {formData.client_name} • Generated {new Date(proposal.generated_at).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <Button 
                variant="outline" 
                size="sm"
                onClick={copyProposal}
                data-testid="proposal-copy-btn"
              >
                {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                Copy
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setProposal(null);
                  setShowForm(true);
                }}
                data-testid="proposal-new-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                New
              </Button>
              {/* Document 2 · Phase 4 — Build Vision Deck */}
              <Button
                size="sm"
                onClick={() => setBuildDeckOpen(true)}
                data-testid="build-deck-btn"
                className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
              >
                <Layers className="h-3 w-3 mr-1" />
                <span className="sm:hidden">Vision Deck</span>
                <span className="hidden sm:inline">Build Vision Deck</span>
              </Button>
              {/* Document 2 · Phase 6 — Build & Send (one-click) */}
              <Button
                size="sm"
                onClick={handleBuildAndSend}
                disabled={buildAndSending}
                data-testid="build-and-send-btn"
                className="bg-amber-700 hover:bg-amber-800 text-white disabled:bg-amber-300"
                title="Build deck draft + dual-tone email drafts in one click"
              >
                {buildAndSending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Zap className="h-3 w-3 mr-1" />
                )}
                Build &amp; Send
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Document 2 · Phase 6.3.2 — Detection Badge (form-side, with override controls) */}
          {(p.business_scale || p.detection_mode) && (
            <DetectionBadge
              proposal={p}
              readOnly={false}
              regenerating={loading}
              onRegenerate={generateProposal}
            />
          )}

          {/* Executive Summary */}
          <div className="p-4 rounded-lg bg-gradient-to-r from-[#B89B5E]/10 to-transparent border border-[#B89B5E]/20">
            <Badge className="mb-2 bg-[#B89B5E] text-white">Executive Summary</Badge>
            <p className="text-sm text-[#1A1A1A]">{p.executive_summary}</p>
          </div>

          {/* The Opportunity */}
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">The Opportunity</h4>
            <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{p.the_opportunity}</p>
          </div>

          {/* The Challenge */}
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">The Challenge</h4>
            <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{p.the_challenge}</p>
          </div>

          {/* The Vision */}
          <div className="p-4 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1]">
            <h4 className="text-sm font-semibold text-[#B89B5E] mb-2">The Vision</h4>
            <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{p.the_vision}</p>
          </div>

          {/* The Plan */}
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">The Plan</h4>
            {Array.isArray(p.the_plan) ? (
              <ol className="space-y-2">
                {p.the_plan.map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#B89B5E]/10 text-[#B89B5E] flex items-center justify-center text-xs font-medium">
                      {i + 1}
                    </span>
                    <span className="text-[#1A1A1A]/80">{step}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{p.the_plan}</p>
            )}
          </div>

          {/* The Result */}
          <div>
            <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2">The Result</h4>
            {Array.isArray(p.the_result) ? (
              <ul className="space-y-1">
                {p.the_result.map((result, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="text-emerald-500">✓</span>
                    <span className="text-[#1A1A1A]/80">{result}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-[#1A1A1A]/80 whitespace-pre-line">{p.the_result}</p>
            )}
          </div>

          {/* Next Step */}
          <div className="p-4 rounded-lg bg-[#1A1A1A] text-white">
            <h4 className="text-sm font-semibold text-[#B89B5E] mb-2">Next Step</h4>
            <p className="text-sm text-white/90">{p.next_step}</p>
          </div>
        </CardContent>
      </Card>

      {/* Document 2 · Phase 4 — Build Vision Deck modal */}
      <BuildDeckPickerModal
        open={buildDeckOpen}
        onOpenChange={setBuildDeckOpen}
        onBuild={handleBuildDeck}
        proposalSummary={`${formData.client_name || 'Client'} · ${formData.industry || ''}`}
      />
      </>
    );
  }

  // Initial state - show generate button
  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]">
      <CardContent className="p-6">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
            <FileText className="h-8 w-8 text-[#B89B5E]" />
          </div>
          <div>
            <h3 className="font-medium text-[#1A1A1A]">Vision Proposal Generator</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Create StoryBrand-structured proposals that resonate
            </p>
          </div>
          <Button 
            onClick={() => setShowForm(true)}
            className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
            data-testid="generate-proposal-btn"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate Vision Proposal
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
